<script setup>
import { computed, ref } from 'vue'
import { pie as d3pie, arc as d3arc } from 'd3-shape'

const props = defineProps({
  // [{ label: 'SSH', value: 42, color?: '#f97316' }, ...]
  data:      { type: Array,  default: () => [] },
  // Slices beyond this are grouped into one "other" slice
  maxSlices: { type: Number, default: 8 },
  // Label for the grouped rest slice
  otherLabel: { type: String, default: 'Other' },
  size:      { type: Number, default: 116 },
  // Raw value of the currently active filter — the matching slice is
  // highlighted, all others are dimmed
  selected:  { type: [String, Number], default: null },
})

const emit = defineEmits(['slice-click'])

// Enginsight-like categorical palette — identical in both theme modes
const PALETTE = [
  '#f97316', '#a855f7', '#3b82f6', '#22c55e', '#eab308', '#ef4444',
  '#06b6d4', '#ec4899', '#8b5cf6', '#14b8a6', '#f59e0b', '#64748b',
]

const hoverIdx = ref(null)

const items = computed(() => {
  const sorted = [...props.data]
    .filter(d => Number(d.value) > 0)
    .sort((a, b) => b.value - a.value)
  if (sorted.length <= props.maxSlices) return sorted
  const head = sorted.slice(0, props.maxSlices - 1)
  const rest = sorted.slice(props.maxSlices - 1)
  head.push({
    label: props.otherLabel,
    value: rest.reduce((s, d) => s + Number(d.value), 0),
    color: '#94a3b8',
    isOther: true,
  })
  return head
})

const total = computed(() => items.value.reduce((s, d) => s + Number(d.value), 0))

const slices = computed(() => {
  const r     = props.size / 2
  const arcGen = d3arc().innerRadius(r * 0.68).outerRadius(r).cornerRadius(1.5)
  const arcsData = d3pie().value(d => d.value).sort(null).padAngle(0.02)(items.value)
  return arcsData.map((a, i) => ({
    d:     arcGen(a),
    color: items.value[i].color ?? PALETTE[i % PALETTE.length],
    item:  items.value[i],
    idx:   i,
  }))
})

function pct(value) {
  if (!total.value) return ''
  return `${Math.round((value / total.value) * 100)}%`
}

function isSelected(item) {
  return props.selected != null && item.raw === props.selected
}

function isDimmed(s) {
  if (hoverIdx.value !== null) return hoverIdx.value !== s.idx
  return props.selected != null && !isSelected(s.item)
}
</script>

<template>
  <div class="dnt-wrap">
    <div v-if="!items.length" class="dnt-empty">—</div>
    <template v-else>
      <svg
        class="dnt-svg"
        :width="size"
        :height="size"
        :viewBox="`${-size / 2} ${-size / 2} ${size} ${size}`"
      >
        <path
          v-for="s in slices"
          :key="s.idx"
          :d="s.d"
          :fill="s.color"
          class="dnt-slice"
          :class="{
            'dnt-slice--dim': isDimmed(s),
            'dnt-slice--selected': isSelected(s.item),
          }"
          @mouseenter="hoverIdx = s.idx"
          @mouseleave="hoverIdx = null"
          @click="emit('slice-click', s.item)"
        >
          <title>{{ s.item.label }}: {{ s.item.value.toLocaleString() }} ({{ pct(s.item.value) }})</title>
        </path>
        <text class="dnt-center" text-anchor="middle" dominant-baseline="central">
          {{ total.toLocaleString() }}
        </text>
      </svg>

      <ul class="dnt-legend">
        <li
          v-for="s in slices"
          :key="s.idx"
          class="dnt-legend-row"
          :class="{
            'dnt-legend-row--hover': hoverIdx === s.idx,
            'dnt-legend-row--selected': isSelected(s.item),
          }"
          @mouseenter="hoverIdx = s.idx"
          @mouseleave="hoverIdx = null"
          @click="emit('slice-click', s.item)"
        >
          <span class="dnt-legend-bar" :style="{ background: s.color }" />
          <span class="dnt-legend-label" :title="s.item.label">{{ s.item.label }}</span>
          <i v-if="isSelected(s.item)" class="pi pi-filter-fill dnt-legend-filter-icon" />
          <span class="dnt-legend-count">{{ s.item.value.toLocaleString() }}</span>
        </li>
      </ul>
    </template>
  </div>
</template>

<style scoped>
.dnt-wrap {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 0;
}
.dnt-empty {
  width: 100%;
  text-align: center;
  padding: 18px 0;
  color: var(--p-text-color-secondary);
  font-size: .85rem;
}
.dnt-svg { flex-shrink: 0; }
.dnt-slice {
  cursor: pointer;
  transition: opacity .15s ease;
}
.dnt-slice--dim { opacity: 0.25; }
.dnt-slice--selected {
  stroke: var(--p-text-color);
  stroke-width: 2;
  paint-order: stroke;
}
.dnt-center {
  fill: var(--p-text-color);
  font-size: 15px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.dnt-legend {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
  min-width: 0;
  max-height: 160px;
  overflow-y: auto;
}
.dnt-legend-row {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 2px 4px;
  border-radius: 4px;
  cursor: pointer;
  transition: background .12s;
}
.dnt-legend-row--hover {
  background: color-mix(in srgb, var(--p-primary-color) 6%, transparent);
}
.dnt-legend-row--selected {
  background: color-mix(in srgb, var(--p-primary-color) 14%, transparent);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--p-primary-color) 35%, transparent);
}
.dnt-legend-row--selected .dnt-legend-label {
  font-weight: 700;
}
.dnt-legend-filter-icon {
  font-size: .55rem;
  color: var(--p-primary-color);
  flex-shrink: 0;
}
.dnt-legend-bar {
  width: 3px;
  height: 12px;
  border-radius: 2px;
  flex-shrink: 0;
}
.dnt-legend-label {
  flex: 1;
  min-width: 0;
  font-size: .72rem;
  color: var(--p-text-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dnt-legend-count {
  font-size: .7rem;
  font-variant-numeric: tabular-nums;
  color: var(--p-text-color-secondary);
  flex-shrink: 0;
}
</style>
